import {
  Add,
  Cancel,
  CheckCircle,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  HourglassBottomOutlined,
} from '@mui/icons-material';
import React from 'react';
import Typography from '@mui/material/Typography';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  TextField,
} from '@mui/material';
import { ModalInner } from '../lower-order';
import moment from 'moment';
import { useFeathers } from '../../app/util';

const ScrapedContentPreview = ({
  scrapedContent,
  onScrapedContentSaved,
  editingEnabled = true,
}) => {
  const [showContent, setShowContent] = React.useState(false);
  const [fullScrapedContent, setFullScrapedContent] = React.useState(null);
  const [loadingContent, setLoadingContent] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [draftPages, setDraftPages] = React.useState([]);
  const [originalPages, setOriginalPages] = React.useState([]);
  const [editingPages, setEditingPages] = React.useState([]);
  const [expandedPages, setExpandedPages] = React.useState([]);
  const [addPageOpen, setAddPageOpen] = React.useState(false);
  const [newPageUrl, setNewPageUrl] = React.useState('');
  const [openInEditMode, setOpenInEditMode] = React.useState(false);
  const [savedMetadata, setSavedMetadata] = React.useState(null);
  const feathers = useFeathers();

  const displayedScrapedContent = savedMetadata || scrapedContent;
  let icon = null, message, clickable;
  if(displayedScrapedContent === null) {
    message = `Not scraped`
  } else if(displayedScrapedContent?.status === 'success') {
    message = `Scraped`;
    icon = <CheckCircle color='success' fontSize={'14px'} />
    clickable = true;
  } else if(displayedScrapedContent?.status === 'error' || displayedScrapedContent?.error) {
    message = `Error`
    icon = <Cancel color='error' fontSize={'14px'} />;
    clickable = true;
  } else if(displayedScrapedContent) {
    message = `Pending`;
    icon = <HourglassBottomOutlined fontSize={'14px'} />
  } else {
    message = `--`;
  }

  const isError = displayedScrapedContent?.status === 'error' || Boolean(displayedScrapedContent?.error);
  const isDirty = JSON.stringify(draftPages) !== JSON.stringify(originalPages);

  const uniquePages = pages => {
    const urls = new Set();
    return (pages || []).filter(page => {
      if(urls.has(page.sourceUrl)) return false;
      urls.add(page.sourceUrl);
      return true;
    });
  };

  const handleOpen = async (editMode = false) => {
    if(!displayedScrapedContent?._id || loadingContent) return;

    setShowContent(true);
    setOpenInEditMode(editMode);
    setFullScrapedContent(null);
    setLoadError(null);
    setSaveError(null);
    setEditingPages([]);
    setExpandedPages([]);
    setLoadingContent(true);

    try {
      const result = await feathers.getService('scraped-content').get(displayedScrapedContent._id);
      const pages = uniquePages(result.content?.bodyText);
      setFullScrapedContent(result);
      setDraftPages(pages);
      setOriginalPages(pages);
      setEditingPages(editMode ? pages.map((_, index) => index) : []);
    } catch(err) {
      console.error('Failed to load scraped content:', err);
      setLoadError(err);
    } finally {
      setLoadingContent(false);
    }
  };

  const updatePageContent = (index, content) => {
    setDraftPages(pages => pages.map((page, pageIndex) => (
      pageIndex === index ? { ...page, content } : page
    )));
  };

  const deletePage = index => {
    setDraftPages(pages => pages.filter((_, pageIndex) => pageIndex !== index));
    setEditingPages(indexes => indexes.filter(pageIndex => pageIndex !== index));
  };

  const addPage = () => {
    const sourceUrl = newPageUrl.trim();
    if(!sourceUrl) return;

    setDraftPages(pages => [...pages, { sourceUrl, content: '' }]);
    setEditingPages(indexes => [...indexes, draftPages.length]);
    setNewPageUrl('');
    setAddPageOpen(false);
  };

  const saveChanges = async () => {
    if(!fullScrapedContent?._id || !isDirty || saving) return;

    setSaving(true);
    setSaveError(null);
    try {
      const result = await feathers.getService('scraped-content').patch(fullScrapedContent._id, {
        content: {
          ...(fullScrapedContent.content || {}),
          bodyText: draftPages,
        },
        addMethod: 'manual',
        error: null,
      });
      const pages = uniquePages(result.content?.bodyText || draftPages);
      setFullScrapedContent(result);
      setDraftPages(pages);
      setOriginalPages(pages);
      setEditingPages([]);
      setOpenInEditMode(false);
      setSavedMetadata(result);
      onScrapedContentSaved?.(result);
    } catch(err) {
      console.error('Failed to save scraped content:', err);
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const urlMain = fullScrapedContent?.url || fullScrapedContent?.content?.resolvedUrl;

  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <Modal
        open={showContent}
        onClose={() => setShowContent(false)}
      >
        <ModalInner
          onClose={() => setShowContent(false)}
          style={{ width: '80vw', height: '80vh', overflow: 'auto' }}
        >
          {loadingContent && <CircularProgress />}
          {loadError && (
            <Alert severity='error'>
              Failed to load scraped content. {String(loadError?.message || loadError)}
            </Alert>
          )}
          {fullScrapedContent && (
            <Typography variant={'body2'} style={{ fontWeight: 'bold', opacity: 0.6, marginBottom: '4px' }}>
              Last updated {moment(fullScrapedContent.updatedAt).fromNow()}
            </Typography>
          )}
          {saveError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              Failed to save scraped content. {String(saveError?.message || saveError)}
            </Alert>
          )}
          {
            fullScrapedContent?.error && !isDirty &&
            <Alert severity='error'>
              {String(fullScrapedContent.error)}{` `}
              {urlMain && <a href={urlMain} target='_blank' rel='noreferrer'>{urlMain.slice(0,100)}</a>}
            </Alert>
          }
          {fullScrapedContent && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {draftPages.map(({ sourceUrl, content }, index) => {
                const isEditing = editingPages.includes(index);
                const isExpanded = expandedPages.includes(index);
                const needsExpansion = (content || '').length > 500 || (content || '').split('\n').length > 4;

                return (
                  <Box key={`${sourceUrl}-${index}`}>
                    <Typography variant='body2' sx={{ mb: 0.5, textAlign: 'left' }}>
                      <a href={sourceUrl} target='_blank' rel='noreferrer'>{sourceUrl}</a>
                    </Typography>
                    <Paper sx={{ position: 'relative', p: 2, pt: 5 }}>
                      {editingEnabled && (
                      <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          aria-label={`Edit ${sourceUrl}`}
                          size='small'
                          onClick={() => setEditingPages(indexes => (
                            indexes.includes(index)
                              ? indexes.filter(pageIndex => pageIndex !== index)
                              : [...indexes, index]
                          ))}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                        <IconButton
                          aria-label={`Delete ${sourceUrl}`}
                          size='small'
                          color='error'
                          onClick={() => deletePage(index)}
                        >
                          <Delete fontSize='small' />
                        </IconButton>
                      </Box>
                      )}
                      {isEditing ? (
                        <TextField
                          value={content || ''}
                          onChange={event => updatePageContent(index, event.target.value)}
                          multiline
                          minRows={8}
                          fullWidth
                          autoFocus={openInEditMode}
                          inputProps={{ 'aria-label': `Content for ${sourceUrl}` }}
                        />
                      ) : (
                        <>
                          {!content && <Typography variant='body1'>No content.</Typography>}
                          {content && (
                            <Collapse in={isExpanded} collapsedSize={96}>
                              <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>{content}</Typography>
                            </Collapse>
                          )}
                          {content && needsExpansion && (
                            <Button
                              size='small'
                              onClick={() => setExpandedPages(indexes => (
                                indexes.includes(index)
                                  ? indexes.filter(pageIndex => pageIndex !== index)
                                  : [...indexes, index]
                              ))}
                              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </Button>
                          )}
                        </>
                      )}
                    </Paper>
                  </Box>
                );
              })}
              {editingEnabled && (
                <Button
                  startIcon={<Add />}
                  onClick={() => setAddPageOpen(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add page
                </Button>
              )}
              {isDirty && (
                <Button
                  variant='contained'
                  onClick={saveChanges}
                  disabled={saving}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              )}
            </Box>
          )}
        </ModalInner>
      </Modal>
      <Dialog open={addPageOpen} onClose={() => setAddPageOpen(false)}>
        <DialogTitle>Add page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label='Page URL'
            value={newPageUrl}
            onChange={event => setNewPageUrl(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPageOpen(false)}>Cancel</Button>
          <Button onClick={addPage} disabled={!newPageUrl.trim()}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <span
        style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: clickable ? 'pointer' : 'default' }}
        onClick={clickable && !showContent ? () => handleOpen(false) : undefined}
      >
      {icon}
      <Typography variant={'subtitle1'} style={{ opacity: 0.6, fontSize: '14px',  }}>{message}</Typography>
      </span>
      {isError && editingEnabled && (
        <Button
          variant='contained'
          size='small'
          onClick={() => handleOpen(true)}
          sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '11px', textTransform: 'none' }}
        >
          Add manually
        </Button>
      )}
    </div>
  )
}

export { ScrapedContentPreview };